import { useCallback, useRef } from 'react';

import { useEventListener } from '@atlassian/react-async';

import { useUniversalCreateAnalytics } from '../../../../hooks';

const MODULE_ID = 'universal-create-popup-content';

export type ResourceLoadEvent = {
	error?: Error;
	moduleId: string;
	transactionId: string;
};

export type ResourceLoadEventCallback = (event: ResourceLoadEvent) => void;

export function useJsChunkLoadTrack(): void {
	const { fireJSChunkLoadTime } = useUniversalCreateAnalytics();

	const hasFiredRef = useRef(false);
	const loadStartTimesRef = useRef<Map<string, number>>(new Map());

	const handleLoadStart = useCallback((event: ResourceLoadEvent) => {
		if (event.moduleId === MODULE_ID && !hasFiredRef.current) {
			loadStartTimesRef.current.set(event.transactionId, performance.now());
		}
	}, []);

	const handleLoadCompleteOrError = useCallback(
		(event: ResourceLoadEvent) => {
			const startTime = loadStartTimesRef.current.get(event.transactionId);

			if (startTime !== undefined) {
				loadStartTimesRef.current.delete(event.transactionId);
			}

			if (event.moduleId === MODULE_ID && startTime !== undefined && !hasFiredRef.current) {
				const loadTime = performance.now() - startTime;
				hasFiredRef.current = true;
				fireJSChunkLoadTime({ durationInMs: loadTime, hasError: Boolean(event.error) });
			}
		},
		[fireJSChunkLoadTime],
	);

	useEventListener('jsresource.load.start', handleLoadStart);
	useEventListener('jsresource.load.complete', handleLoadCompleteOrError);
	useEventListener('jsresource.load.error', handleLoadCompleteOrError);
}
